/**
 * Ticket 114.3 — Generic horizontal stepper primitive.
 *
 * Reused by OpportunityCreateDemoSheet (3 steps) and DemoStudioPage (9 steps).
 * Accessible: role="list" on the container, role="listitem" per step,
 * aria-current="step" on the active step.
 */

import { Check } from 'lucide-react';
import { cn } from '@/lib/cn';

export interface StepperStep {
  key: string;
  label: string;
  /** Whether the step's form data is valid (used to style done/pending). */
  valid?: boolean;
}

interface StepperProps {
  steps: StepperStep[];
  currentIndex: number;
  onStepClick?: (index: number) => void;
}

export function Stepper({ steps, currentIndex, onStepClick }: StepperProps) {
  return (
    <nav aria-label="Progress">
      <ol role="list" className="flex items-center gap-0">
        {steps.map((step, index) => {
          const isDone = index < currentIndex;
          const isActive = index === currentIndex;
          const isPending = index > currentIndex;
          const isClickable = onStepClick != null && isDone;

          return (
            <li
              key={step.key}
              role="listitem"
              aria-current={isActive ? 'step' : undefined}
              className="flex items-center flex-1 min-w-0"
            >
              <button
                type="button"
                onClick={isClickable ? () => onStepClick(index) : undefined}
                disabled={!isClickable}
                className={cn(
                  'flex flex-col items-center gap-1 flex-1 min-w-0 px-1 py-1',
                  'disabled:cursor-default disabled:pointer-events-none',
                  isClickable && 'cursor-pointer',
                )}
                aria-label={step.label}
              >
                {/* Circle indicator */}
                <span
                  className={cn(
                    'flex h-7 w-7 items-center justify-center rounded-full border-2 text-xs font-semibold transition-colors shrink-0',
                    isDone &&
                      'border-[color:var(--color-lop-teal)] bg-[color:var(--color-lop-teal)] text-white',
                    isActive &&
                      'border-[color:var(--color-lop-teal)] bg-background text-[color:var(--color-lop-teal)]',
                    isPending && 'border-muted-foreground/30 bg-background text-muted-foreground/50',
                  )}
                >
                  {isDone ? <Check className="h-3.5 w-3.5" /> : index + 1}
                </span>

                {/* Label — only visible in expanded mode */}
                <span
                  className={cn(
                    'text-xs text-center truncate w-full leading-tight transition-colors',
                    isActive && 'text-foreground font-medium',
                    isDone && 'text-[color:var(--color-lop-teal)]',
                    isPending && 'text-muted-foreground/50',
                  )}
                >
                  {step.label}
                </span>
              </button>

              {/* Connector line — not rendered after last step */}
              {index < steps.length - 1 && (
                <span
                  aria-hidden="true"
                  className={cn(
                    'h-0.5 w-full shrink',
                    index < currentIndex
                      ? 'bg-[color:var(--color-lop-teal)]'
                      : 'bg-muted-foreground/20',
                  )}
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
